import { z } from 'zod';
import { FREE_SAMPLE } from '../lib/free-tier';
import { FreeSampleOpportunitySchema } from './free-sample-opportunity';

/**
 * Scan API Request/Response Validation Schemas
 *
 * These schemas complement scan.contract.ts by providing
 * runtime validation for API payloads.
 * Note: ScanJob type is exported from contracts.scan, not here
 */

/**
 * sample goal configuration for free-tier scans.
 * Defines the stopping criteria used by the scan worker.
 */
export const SmartSampleGoalsSchema = z.object({
  attention_min: z.number().int().nonnegative().default(5),
  archive_min: z.number().int().nonnegative().default(10),
  trash_min: z.number().int().nonnegative().default(10),
  category_diversity_min: z.number().int().nonnegative().default(2),
  hard_ceiling: z.number().int().nonnegative().default(FREE_SAMPLE.fetchTotal),
});

export type SmartSampleGoals = z.infer<typeof SmartSampleGoalsSchema>;

/**
 * sample progress snapshot written into the scan job after completion.
 */
export const SmartSampleProgressSchema = z.object({
  attention_count: z.number().int().nonnegative(),
  archive_count: z.number().int().nonnegative(),
  trash_count: z.number().int().nonnegative(),
  attention_categories: z.number().int().nonnegative(),
  archive_categories: z.number().int().nonnegative(),
  trash_categories: z.number().int().nonnegative(),
  goals_met: z.boolean(),
  ceiling_reached: z.boolean(),
  // Issue #515 sender-centric sample. Optional/back-compat: older completed
  // jobs never wrote these.
  senders_analyzed: z.number().int().nonnegative().optional(),
  classify_target: z.number().int().nonnegative().optional(),
});

export type SmartSampleProgress = z.infer<typeof SmartSampleProgressSchema>;

/**
 * POST /v1/scan/start - Request validation
 */
export const StartScanRequestSchema = z.object({
  scan_mode: z.enum(['full', 'delta']).optional().default('full'),
  intent: z.enum(['manual', 'automatic']).optional(),
});

export const ScanSubmissionIntentSchema = z.enum([
  'manual', 'auto_full_scan', 'auto_stale_refresh', 'auto_scheduled',
  'auto_first_login', 'auto_backlog', 'legacy_unspecified',
]);
export type ScanSubmissionIntent = z.infer<typeof ScanSubmissionIntentSchema>;
export const ScanTerminalFailureCodeSchema = z.enum([
  'HISTORY_INCOMPLETE', 'PROVIDER_RETRY_EXHAUSTED', 'WORKER_NO_PROGRESS_EXHAUSTED', 'PIPELINE_ERROR',
]);
export type ScanTerminalFailureCode = z.infer<typeof ScanTerminalFailureCodeSchema>;
export const ScanItemFailureCodeSchema = z.enum([
  'INVALID_MESSAGE_DATE', 'INVALID_MESSAGE_DATA', 'INVALID_CLASSIFICATION_RESULT', 'HISTORY_UNAVAILABLE',
]);

export type StartScanRequest = z.infer<typeof StartScanRequestSchema>;

/**
 * POST /v1/scan/start - Response validation
 */
export const StartScanResponseSchema = z.object({
  job_id: z.string().uuid(),
  status: z.enum(['PENDING']),
  message: z.string(),
});

export type StartScanResponse = z.infer<typeof StartScanResponseSchema>;

/**
 * POST /v1/scan/reset-sample - Response validation
 *
 * Self-service full reset: wipes the caller's scan-derived data (emails,
 * suggestions, sender insights, exclusion hints, apply jobs, scan jobs) and
 * enqueues a fresh sample scan. `cleared` maps each wiped table to the number
 * of rows removed.
 */
export const ResetSampleScanResponseSchema = z.object({
  job_id: z.string().uuid(),
  status: z.literal('PENDING'),
  cleared: z.record(z.string(), z.number().int().nonnegative()),
  message: z.string(),
});

export type ResetSampleScanResponse = z.infer<typeof ResetSampleScanResponseSchema>;

/**
 * POST /v1/scan/:id/pause - Response validation
 */
export const PauseScanResponseSchema = z.object({
  job_id: z.string().uuid(),
  status: z.literal('pause_requested'),
  message: z.string(),
});

export type PauseScanResponse = z.infer<typeof PauseScanResponseSchema>;

/**
 * POST /v1/scan/:id/resume - Response validation
 */
export const ResumeScanResponseSchema = z.object({
  job_id: z.string().uuid(),
  status: z.literal('IN_PROGRESS'),
  resumed_at: z.string().datetime(),
  message: z.string(),
});

export type ResumeScanResponse = z.infer<typeof ResumeScanResponseSchema>;

/**
 * POST /v1/scan/:id/cancel - Response validation
 */
export const CancelScanResponseSchema = z.object({
  job_id: z.string().uuid(),
  status: z.literal('CANCELED'),
  canceled_at: z.string().datetime(),
  message: z.string(),
});

export type CancelScanResponse = z.infer<typeof CancelScanResponseSchema>;

const DatabaseTimestampSchema = z.string().datetime({ offset: true });

export const MailboxTotalsSchema = z.object({
  messages_total: z.number().int().nonnegative(),
  threads_total: z.number().int().nonnegative(),
  inbox_messages_total: z.number().int().nonnegative(),
  inbox_threads_total: z.number().int().nonnegative(),
  inbox_unread_total: z.number().int().nonnegative(),
  scope_messages_total: z.number().int().nonnegative(),
  scope_precision: z.enum(['derived', 'floored']),
  fetched_at: z.string().datetime({ offset: true }),
});

export type MailboxTotals = z.infer<typeof MailboxTotalsSchema>;

/**
 * GET /v1/scan/current - Response validation
 * Note: Uses ScanJobDataSchema (not exported, only used internally here)
 */
export const ScanJobDataSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  status: z.enum(['PENDING', 'IN_PROGRESS', 'PAUSED', 'COMPLETED', 'FAILED', 'CANCELED']),
  // Issue #804: 'backlog' is scheduler-created only (auto-scan-scheduler.ts) -
  // never accepted by StartScanRequestSchema below, which intentionally still
  // only allows 'full'/'delta' since backlog jobs are never user-initiated.
  scan_mode: z.enum(['full', 'delta', 'backlog']),
  triggered_by: z.enum(['MANUAL', 'AUTO_FIRST_LOGIN', 'AUTO_SCHEDULED']),
  pipeline_version: z.union([z.literal(1), z.literal(2)]).default(1).optional(),
  phase: z.enum(['phase_a', 'phase_b', 'done']).nullable().default(null).optional(),
  listing_complete: z.boolean().nullable().default(null).optional(),
  counts_verified: z.boolean().default(false).optional(),
  last_progress_at: DatabaseTimestampSchema.nullable().default(null).optional(),
  next_run_at: DatabaseTimestampSchema.nullable().default(null).optional(),
  wait_reason: z.enum(['capacity', 'rate_limited', 'provider_unavailable', 'continuation', 'transient_failure']).nullable().default(null).optional(),
  interruption_count: z.number().int().nonnegative().default(0).optional(),
  recovery_notice_at: DatabaseTimestampSchema.nullable().default(null).optional(),
  completion_notification_requested: z.boolean().default(false).optional(),
  completion_notification_available: z.boolean().default(false).optional(),
  fetch_skipped_count: z.number().int().nonnegative().default(0).optional(),
  classification_skipped_count: z.number().int().nonnegative().default(0).optional(),
  history_complete: z.boolean().nullable().default(null).optional(),
  completion_quality: z.enum(['complete', 'partial']).nullable().default(null).optional(),
  terminal_failure_code: ScanTerminalFailureCodeSchema.nullable().default(null).optional(),
  lease_generation: z.number().int().nonnegative().optional(),
  emails_listed: z.number().int().nonnegative(),
  emails_fetched: z.number().int().nonnegative(),
  emails_classified: z.number().int().nonnegative(),
  scanned_emails_size_bytes: z.number().int().nonnegative(),
  resume_page_token: z.string().nullable(),
  last_history_id: z.union([z.number().int().nonnegative(), z.string().regex(/^\d+$/)]).nullable(),
  claimed_by_worker_id: z.string().nullable(),
  claimed_at: DatabaseTimestampSchema.nullable(),
  retry_count: z.number().int().nonnegative(),
  error_message: z.string().nullable(),
  failed_message_ids: z.array(z.string()).default([]),
  total_classification_failures: z.number().int().nonnegative(),
  pause_requested_at: DatabaseTimestampSchema.nullable(),
  pause_reason: z.enum(['user_requested', 'token_refresh_failed', 'rate_limited']).nullable(),
  paused_at: DatabaseTimestampSchema.nullable(),
  resumed_at: DatabaseTimestampSchema.nullable(),
  canceled_at: DatabaseTimestampSchema.nullable(),
  limit_reached: z.boolean().default(false),
  goals_met: z.boolean().default(false),
  smart_sample_progress: SmartSampleProgressSchema.nullable().default(null),
  smart_sample_target: z.number().int().positive().nullable().optional().default(null),
  attention_count: z.number().int().nonnegative().optional().default(0),
  archive_count: z.number().int().nonnegative().optional().default(0),
  trash_count: z.number().int().nonnegative().optional().default(0),
  // Issue #515 sender-centric sample. All nullable/optional for back-compat
  // with rows written before the migration.
  sample_classify_target: z.number().int().nonnegative().nullable().optional().default(null),
  sample_complete: z.boolean().optional().default(false),
  senders_analyzed: z.number().int().nonnegative().nullable().optional().default(null),
  mailbox_totals: MailboxTotalsSchema.nullable().optional().default(null),
  free_sample_opportunity: FreeSampleOpportunitySchema.nullable(),
  created_at: DatabaseTimestampSchema,
  started_at: DatabaseTimestampSchema.nullable(),
  completed_at: DatabaseTimestampSchema.nullable(),
  track_counts: z.object({
    attention: z.number().int().nonnegative(),
    archive: z.number().int().nonnegative(),
    trash: z.number().int().nonnegative(),
  }).optional().nullable(),
  total_suggestions: z.number().int().nonnegative().optional(),
});

export const GetCurrentScanResponseSchema = ScanJobDataSchema.nullable();

export type GetCurrentScanResponse = z.infer<typeof GetCurrentScanResponseSchema>;

/**
 * GET /v1/scan/history - Request validation
 */
export const ListScanHistoryRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().nonnegative().optional().default(0),
  status: z.enum(['PENDING', 'IN_PROGRESS', 'PAUSED', 'COMPLETED', 'FAILED', 'CANCELED']).optional(),
});

export type ListScanHistoryRequest = z.infer<typeof ListScanHistoryRequestSchema>;

/**
 * GET /v1/scan/history - Response validation
 */
export const ListScanHistoryResponseSchema = z.object({
  jobs: z.array(ScanJobDataSchema),
  total: z.number().int().nonnegative(),
});

export type ListScanHistoryResponse = z.infer<typeof ListScanHistoryResponseSchema>;

/**
 * GET /v1/scan/:id - Response validation
 */
export const GetScanJobResponseSchema = ScanJobDataSchema;

export type GetScanJobResponse = z.infer<typeof GetScanJobResponseSchema>;

/**
 * POST /v1/scan/:id/retry-failed - Request validation
 */
export const RetryFailedRequestSchema = z.object({
  message_ids: z.array(z.string()).optional(),
});

export type RetryFailedRequest = z.infer<typeof RetryFailedRequestSchema>;

/**
 * POST /v1/scan/:id/retry-failed - Response validation
 */
export const RetryFailedResponseSchema = z.object({
  retried: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  still_failed: z.array(z.string()).default([]),
});

export type RetryFailedResponse = z.infer<typeof RetryFailedResponseSchema>;

/**
 * GET /v1/scan/worker/health - Response validation
 */
export const WorkerHealthResponseSchema = z.object({
  status: z.enum(['healthy', 'degraded', 'unhealthy']),
  worker_id: z.string(),
  uptime_seconds: z.number().int().nonnegative(),
  jobs_processed_total: z.number().int().nonnegative(),
  jobs_in_progress: z.number().int().nonnegative(),
  last_job_completed_at: z.string().datetime().nullable(),
  last_heartbeat_at: z.string().datetime(),
});

export type WorkerHealthResponse = z.infer<typeof WorkerHealthResponseSchema>;
